import { type ColumnDef } from "@tanstack/react-table";
import { CheckIcon, CopyIcon, KeyRoundIcon, Loader2Icon, PlusIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { AgentAuditEntry, AgentKey, AgentKeyList, AgentKeysClient, IssuedAgentKey } from "./types";

/*
 * Agent API keys: list, create (secret shown once), rotate, revoke, and the
 * audit log of agent calls. Talks to the product only through `client`, so
 * any product that mounts @betterfield/mcp-express's key-management router
 * can import it as is.
 */

interface AgentKeysManagerProps {
  client: AgentKeysClient;
  /** Base URL of the agent API, shown with a new key (e.g. https://api…/api/agent). */
  agentBaseUrl: string;
}

const EXPIRY_OPTIONS = [30, 90, 180, 365];

export function AgentKeysManager({ client, agentBaseUrl }: AgentKeysManagerProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<AgentKeyList | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedAgentKey | null>(null);
  const [pending, setPending] = useState<{ action: "rotate" | "revoke"; key: AgentKey } | null>(null);
  const [acting, setActing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await client.list());
    } catch {
      toast.error(t("common.api-error"));
    } finally {
      setLoading(false);
    }
  }, [client, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const keyNames = useMemo(() => new Map((data?.items ?? []).map((key) => [key.id, key.name])), [data]);

  const columns = useMemo<ColumnDef<AgentKey>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("agent-keys.columns.name"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <KeyRoundIcon className="size-4 text-muted-foreground" aria-hidden />
            <span className="font-medium">{row.original.name}</span>
            <code className="text-xs text-muted-foreground">…{row.original.id.slice(-6)}</code>
          </div>
        ),
      },
      {
        accessorKey: "scopes",
        header: t("agent-keys.columns.scopes"),
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.scopes.map((scope) => (
              <Badge key={scope} variant="outline">
                {scope}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: t("agent-keys.columns.status"),
        cell: ({ row }) => <StatusBadge keyItem={row.original} />,
      },
      {
        accessorKey: "lastUsedAt",
        header: t("agent-keys.columns.last-used"),
        cell: ({ row }) => formatDate(row.original.lastUsedAt) ?? t("agent-keys.never-used"),
      },
      {
        accessorKey: "expiresAt",
        header: t("agent-keys.columns.expires"),
        cell: ({ row }) => formatDate(row.original.expiresAt),
      },
      {
        accessorKey: "createdBy",
        header: t("agent-keys.columns.created-by"),
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.createdBy} · {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) =>
          row.original.status === "active" ? (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("agent-keys.rotate.action")}
                title={t("agent-keys.rotate.action")}
                onClick={() => setPending({ action: "rotate", key: row.original })}
              >
                <RefreshCwIcon className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("agent-keys.revoke.action")}
                title={t("agent-keys.revoke.action")}
                onClick={() => setPending({ action: "revoke", key: row.original })}
              >
                <Trash2Icon className="size-4" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [t],
  );

  async function confirmPending() {
    if (!pending) return;
    setActing(true);
    try {
      if (pending.action === "rotate") {
        setIssued(await client.rotate(pending.key.id));
      } else {
        await client.revoke(pending.key.id);
        toast.success(t("agent-keys.revoke.success", { name: pending.key.name }));
      }
      await reload();
    } catch {
      toast.error(t("common.api-error"));
    } finally {
      setActing(false);
      setPending(null);
    }
  }

  return (
    <>
      <Tabs defaultValue="keys">
        <div className="flex items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="keys">{t("agent-keys.tabs.keys")}</TabsTrigger>
            <TabsTrigger value="audit">{t("agent-keys.tabs.audit")}</TabsTrigger>
          </TabsList>
          <Button onClick={() => setCreateOpen(true)} disabled={!data}>
            <PlusIcon className="size-4" />
            {t("agent-keys.create.action")}
          </Button>
        </div>

        <TabsContent value="keys" className="mt-4">
          <DataTable
            columns={columns}
            data={data?.items ?? []}
            emptyMessage={t("agent-keys.empty")}
            loading={loading && !data}
            getRowClassName={(row) => (row.original.status === "active" ? undefined : "opacity-60")}
          />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AuditLog client={client} keyNames={keyNames} />
        </TabsContent>
      </Tabs>

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        list={data}
        onCreate={async (input) => {
          const result = await client.create(input);
          setCreateOpen(false);
          setIssued(result);
          await reload();
        }}
      />

      <IssuedKeyDialog issued={issued} agentBaseUrl={agentBaseUrl} onClose={() => setIssued(null)} />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={
          pending?.action === "rotate"
            ? t("agent-keys.rotate.title", { name: pending?.key.name })
            : t("agent-keys.revoke.title", { name: pending?.key.name })
        }
        description={
          pending?.action === "rotate" ? t("agent-keys.rotate.description") : t("agent-keys.revoke.description")
        }
        confirmLabel={pending?.action === "rotate" ? t("agent-keys.rotate.action") : t("agent-keys.revoke.action")}
        destructive={pending?.action === "revoke"}
        onConfirm={confirmPending}
        isPending={acting}
      />
    </>
  );
}

function StatusBadge({ keyItem }: { keyItem: AgentKey }) {
  const { t } = useTranslation();
  if (keyItem.status === "revoked") return <Badge variant="destructive">{t("agent-keys.status.revoked")}</Badge>;
  if (keyItem.status === "expired") return <Badge variant="secondary">{t("agent-keys.status.expired")}</Badge>;
  if (keyItem.replacedBy) return <Badge variant="warning">{t("agent-keys.status.rotating")}</Badge>;
  return <Badge variant="success">{t("agent-keys.status.active")}</Badge>;
}

interface CreateKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: AgentKeyList | null;
  onCreate: (input: { name: string; scopes: string[]; expiresInDays: number }) => Promise<void>;
}

function CreateKeyDialog({ open, onOpenChange, list, onCreate }: CreateKeyDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setScopes([]);
      setExpiresInDays(90);
    }
  }, [open]);

  const valid = name.trim().length > 0 && scopes.length > 0;

  async function submit() {
    if (!valid) return;
    setSaving(true);
    try {
      await onCreate({ name: name.trim(), scopes, expiresInDays });
    } catch {
      toast.error(t("common.api-error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("agent-keys.create.title")}</DialogTitle>
          <DialogDescription>{t("agent-keys.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="agent-key-name">{t("agent-keys.create.name")}</Label>
            <Input
              id="agent-key-name"
              value={name}
              placeholder={t("agent-keys.create.name-placeholder")}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>{t("agent-keys.create.scopes")}</Label>
            {(list?.scopes ?? []).map((scope) => (
              <label key={scope.name} className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={scopes.includes(scope.name)}
                  onCheckedChange={(checked) =>
                    setScopes((current) =>
                      checked ? [...current, scope.name] : current.filter((name) => name !== scope.name),
                    )
                  }
                />
                <span>
                  <code>{scope.name}</code>
                  {scope.description ? <span className="text-muted-foreground"> · {scope.description}</span> : null}
                </span>
              </label>
            ))}
          </div>

          <div className="grid gap-2">
            <Label>{t("agent-keys.create.expiry")}</Label>
            <Select value={String(expiresInDays)} onValueChange={(value) => setExpiresInDays(Number(value))}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    {t("agent-keys.create.expiry-days", { count: days })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? <Loader2Icon className="size-4 animate-spin" /> : null}
            {t("agent-keys.create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssuedKeyDialog({
  issued,
  agentBaseUrl,
  onClose,
}: {
  issued: IssuedAgentKey | null;
  agentBaseUrl: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={issued !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("agent-keys.issued.title", { name: issued?.key.name })}</DialogTitle>
          <DialogDescription>{t("agent-keys.issued.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <CopyField label={t("agent-keys.issued.key")} value={issued?.secret ?? ""} />
          <CopyField label={t("agent-keys.issued.rest-url")} value={agentBaseUrl} />
          <CopyField label={t("agent-keys.issued.mcp-url")} value={`${agentBaseUrl}/mcp`} />
        </div>

        <DialogFooter>
          <Button onClick={onClose}>{t("agent-keys.issued.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("agent-keys.issued.copy-failed"));
    }
  }

  return (
    <div className="grid gap-1">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-xs" onFocus={(event) => event.target.select()} />
        <Button variant="outline" size="icon" onClick={copy} aria-label={t("agent-keys.issued.copy")}>
          {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
        </Button>
      </div>
    </div>
  );
}

const AUDIT_PAGE_SIZE = 50;

function AuditLog({ client, keyNames }: { client: AgentKeysClient; keyNames: Map<string, string> }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AgentAuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (from?: string) => {
      setLoading(true);
      try {
        const page = await client.audit({ limit: AUDIT_PAGE_SIZE, cursor: from });
        setEntries((current) => (from ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
      } catch {
        toast.error(t("common.api-error"));
      } finally {
        setLoading(false);
      }
    },
    [client, t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<AgentAuditEntry>[]>(
    () => [
      { accessorKey: "at", header: t("agent-keys.audit.at"), cell: ({ row }) => formatDate(row.original.at, true) },
      {
        accessorKey: "keyId",
        header: t("agent-keys.audit.key"),
        cell: ({ row }) => keyNames.get(row.original.keyId) ?? `…${row.original.keyId.slice(-6)}`,
      },
      {
        accessorKey: "channel",
        header: t("agent-keys.audit.channel"),
        cell: ({ row }) => <Badge variant="outline">{row.original.channel.toUpperCase()}</Badge>,
      },
      { accessorKey: "operation", header: t("agent-keys.audit.operation"), cell: ({ row }) => <code>{row.original.operation}</code> },
      {
        accessorKey: "status",
        header: t("agent-keys.audit.status"),
        cell: ({ row }) => (
          <Badge variant={row.original.status < 400 ? "success" : row.original.status < 500 ? "warning" : "destructive"}>
            {row.original.status}
          </Badge>
        ),
      },
      { accessorKey: "durationMs", header: t("agent-keys.audit.duration"), cell: ({ row }) => `${row.original.durationMs} ms` },
    ],
    [t, keyNames],
  );

  return (
    <div className="grid gap-3">
      <DataTable
        columns={columns}
        data={entries}
        emptyMessage={t("agent-keys.audit.empty")}
        loading={loading && entries.length === 0}
        pagination={false}
      />
      {cursor ? (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => load(cursor)} disabled={loading}>
            {loading ? <Loader2Icon className="size-4 animate-spin" /> : null}
            {t("agent-keys.audit.load-more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function formatDate(value: string | undefined, withTime = false): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return withTime ? date.toLocaleString() : date.toLocaleDateString();
}
